import React, { useEffect } from 'react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { SEOHero, SEOSection, FeatureGrid, BenefitList, InternalLinkGrid, CTABanner } from '../../components/SEOPageComponents';
import { Hotel, Receipt, Shield, Zap, BarChart3, Users, Clock, Building2 } from 'lucide-react';

export default function HotelEInvoiceSystem() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="Hotel e-Invoice System — Automated LHDN Invoicing for Hotels"
        description="StayMii is a hotel e-invoice system that automates LHDN-compliant invoicing for hotels, resorts, and service apartments. Integrates with your PMS and channel manager."
        keywords="hotel e invoice system, hotel e invoice, hotel invoicing system malaysia, hotel lhdn e invoice, hotel myinvois, hotel billing system e invoice, resort e invoice system"
        canonicalUrl="/hotel-e-invoice-system"
      />
      <div className="min-h-screen bg-white">
        <LandingNavBar />
        <SEOHero
          badge="Hotel e-Invoice"
          h1="Hotel e-Invoice System: LHDN-Compliant Invoicing for Hotels & Resorts"
          subtitle="StayMii automates e-invoice generation and LHDN submission for hotels. Connect your PMS, sync OTA bookings, and let StayMii handle compliance — from guest billing to tax submission."
        />

        <SEOSection h2="Why Hotels Need a Dedicated e-Invoice System" description="Generic e-invoice tools don't understand hotel operations. StayMii is purpose-built for hospitality — handling room bookings, OTA reservations, service charges, and multi-property management.">
          <FeatureGrid items={[
            { icon: Hotel, title: 'Built for Hotel Operations', desc: 'Handles room bookings, walk-ins, OTA reservations, service charges, and ancillary revenue. Designed by hoteliers, for hoteliers.' },
            { icon: Receipt, title: 'Automated Guest Billing', desc: 'Generate e-invoices automatically from check-in/check-out data. Every guest transaction becomes a compliant invoice.' },
            { icon: Shield, title: 'LHDN MyInvois Compliant', desc: 'Every invoice meets LHDN specifications. SST, tourism tax, and service charges are calculated automatically.' },
            { icon: Building2, title: 'Multi-Property Support', desc: 'Manage e-invoice compliance for multiple hotels from a single dashboard. Centralised reporting and analytics.' },
            { icon: BarChart3, title: 'Revenue & Compliance Dashboard', desc: 'Real-time visibility into invoice status, submission rates, and revenue across all properties.' },
            { icon: Clock, title: 'Go Live in Days', desc: 'Connect your PMS and start generating compliant e-invoices immediately. No complex migration needed.' },
          ]} />
        </SEOSection>

        <SEOSection h2="Hotel e-Invoice Use Cases" bg="bg-slate-50">
          <BenefitList items={[
            { title: 'OTA Booking Invoices', desc: 'Automatically generate e-invoices for Booking.com, Agoda, Expedia, and other OTA reservations synced via your channel manager.' },
            { title: 'Direct Booking & Walk-In Invoices', desc: 'PMS-integrated invoicing for direct website bookings and walk-in guests with real-time tax calculation.' },
            { title: 'F&B and Ancillary Revenue', desc: 'Capture restaurant, spa, minibar, and other ancillary charges for comprehensive e-invoicing.' },
            { title: 'Group Bookings & Corporate Billing', desc: 'Handle group reservations and corporate billing with bulk invoice generation and custom payment terms.' },
            { title: 'Credit Notes & Refunds', desc: 'Process refunds, cancellations, and rate adjustments with LHDN-compliant credit and debit notes.' },
          ]} />
        </SEOSection>

        <SEOSection h2="Related Solutions">
          <InternalLinkGrid links={[
            { to: '/e-invoice-malaysia', title: 'e-Invoice Malaysia', desc: 'Complete e-invoicing guide' },
            { to: '/lhdn-e-invoice', title: 'LHDN e-Invoice', desc: 'LHDN compliance guide' },
            { to: '/pms-e-invoice-integration', title: 'PMS e-Invoice Integration', desc: 'Connect your hotel PMS' },
            { to: '/channel-manager-e-invoice', title: 'Channel Manager e-Invoice', desc: 'OTA booking automation' },
            { to: '/airbnb-e-invoice-malaysia', title: 'Airbnb e-Invoice', desc: 'For short-term rental operators' },
            { to: '/myinvois-api', title: 'MyInvois API', desc: 'Technical API integration' },
          ]} />
        </SEOSection>

        <CTABanner title="Get Your Hotel e-Invoice System Running Today" subtitle="Join hotels across Malaysia using StayMii for automated LHDN e-invoicing." />
        <LandingFooter />
      </div>
    </>
  );
}
